/* eslint-disable react/prop-types */
import React, { useMemo, useState, useCallback } from 'react';
import './SimpleGrid.scss';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Input } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
// Note: Fullscreen is handled by the dashboard overlay, not inside this component

// Build column descriptors from metadata (strings or objects)
const buildColumns = (metadata = [], sampleRow = [], defaultWidth = 160) => {
  if (!Array.isArray(metadata) || metadata.length === 0) {
    const count = Array.isArray(sampleRow) ? sampleRow.length : 0;
    return new Array(count).fill(0).map((_, i) => ({
      title: `Column ${i + 1}`,
      field: `col_${i + 1}`,
      width: defaultWidth
    }));
  }

  const getTitle = (m, idx) => {
    if (typeof m === 'string') return m;
    return (
      m?.display_name || m?.column || m?.field || m?.name || `Column ${idx + 1}`
    );
  };
  const toFieldKey = (title, idx) => {
    const base = (title || `Column ${idx + 1}`)
      .toString()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_]/g, '');
    return base || `col_${idx + 1}`;
  };

  return metadata.map((m, i) => {
    const title = getTitle(m, i);
    const width =
      (typeof m === 'object' && (m.width || m.minWidth)) || defaultWidth;
    // Optional color from metadata e.g. { column:'Status', color:'#8d0b93' }
    const color = typeof m === 'object' ? (m.color || m.colour) : undefined;
    return { title, field: toFieldKey(title, i), width, color };
  });
};

// Convert values (2D array) to objects keyed by columns
const buildRows = (values = [], columns = []) => {
  if (!Array.isArray(values)) return [];
  return values.map((rowArr, rIdx) => {
    const obj = { __row: rIdx };
    columns.forEach((col, cIdx) => {
      obj[col.field] = Array.isArray(rowArr) ? rowArr[cIdx] : null;
    });
    return obj;
  });
};

const SimpleGrid = ({
  metadata = [],
  values = [],
  columnWidth = 160,
  height = 220,
  pageSize = 10,
  message
}) => {
  const columns = useMemo(
    () => buildColumns(metadata, values?.[0] || [], columnWidth),
    [metadata, values, columnWidth]
  );
  const rows = useMemo(() => buildRows(values, columns), [values, columns]);

  // Client-side search across all visible columns
  const [query, setQuery] = useState('');
  const normalizedQuery = (query || '').toString().trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedQuery) return rows;
    return rows.filter(r =>
      columns.some(col => {
        const v = r[col.field];
        if (v == null) return false;
        return String(v).toLowerCase().includes(normalizedQuery);
      })
    );
  }, [rows, columns, normalizedQuery]);

  // Pagination (client-side)
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(Math.max(5, Number(pageSize) || 10));
  const onPageChange = useCallback(e => {
    setSkip(e.page.skip);
    setTake(e.page.take);
  }, []);
  const paged = useMemo(() => filtered.slice(skip, skip + take), [filtered, skip, take]);

  // CSV export (current filtered data)
  const downloadCsv = useCallback(() => {
    try {
      const headers = columns.map(c => c.title);
      const csvRows = [headers];
      filtered.forEach(r => {
        const row = columns.map(c => {
          const val = r[c.field];
          if (val == null) return '';
          const s = String(val);
          // Escape quotes and commas
          const needsQuotes = /[",\n]/.test(s);
          const escaped = s.replace(/"/g, '""');
          return needsQuotes ? `"${escaped}"` : escaped;
        });
        csvRows.push(row);
      });
      const csv = csvRows.map(arr => arr.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'grid-data.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_) {}
  }, [columns, filtered]);

  // Search visibility (icon-based)
  const [showSearch, setShowSearch] = useState(false);
  const toggleSearch = useCallback(() => setShowSearch(s => !s), []);

  // Reset pagination when filter changes
  React.useEffect(() => {
    setSkip(0);
  }, [normalizedQuery, take]);
  const isFillHeight = height === '100%' || height === 'fill';

  // Treat a "loading" message with no rows as a special state where we
  // render a centered loading indicator instead of the empty Kendo Grid
  // (which would otherwise show "No records available"). This mirrors the
  const isLoadingMessage =
    typeof message === 'string' && message.toLowerCase().includes('loading');
  const hasAnyRows = filtered.length > 0;

  if (isLoadingMessage && !hasAnyRows) {
    const centerStyle = isFillHeight
      ? {
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      : {
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        };

    return (
      <div
        className="k-p-2"
        style={{
          overflowX: 'auto',
          fontSize: 12,
          lineHeight: 1.2,
          height: isFillHeight ? '100%' : undefined,
          display: isFillHeight ? 'flex' : 'block',
          flexDirection: isFillHeight ? 'column' : undefined
        }}
      >
        <div className="tile-loading-message" style={centerStyle}>
          {message || 'Loading records…'}
        </div>
      </div>
    );
  }

  const gridProps = {
    size: 'small',
    className: 'gp-grid Compact smartchat-grid',
    pageable: { buttonCount: 3, pageSizes: [5, 10, 20, 50], info: true },
    total: filtered.length,
    skip,
    take,
    onPageChange,
    style: { height: isFillHeight ? '100%' : height }
  };

  const renderGrid = (dataItems, styleOverrides = {}) => {
    // While the dashboard is still fetching data for this tile, SmartChatDashboard
    // passes a friendly loading message (e.g. "Loading records…"). Use that to
    // override the default Kendo "No records available" text so users see a
    // clearer state while data is in-flight.
    const isLoadingMessage =
      typeof message === 'string' && message.toLowerCase().includes('loading');

    return (
      <Grid
        data={dataItems}
        {...gridProps}
        style={{ ...gridProps.style, ...styleOverrides }}
        noRecords={isLoadingMessage ? message : undefined}
      >
      {columns.map(c => {
        const headerProps = c.color
          ? {
              headerCell: props => (
                <th {...props.tdProps} style={{ ...(props.style || {}), color: c.color }}>
                  <span className="k-column-title">{c.title}</span>
                </th>
              )
            }
          : {};
        return (
          <GridColumn
            key={c.field}
            field={c.field}
            title={c.title}
            width={c.width}
            {...headerProps}
          />
        );
      })}
      </Grid>
    );
  };

  return (
    <div
      className="k-p-2"
      style={{
        overflowX: 'auto',
        fontSize: 12,
        lineHeight: 1.2,
        ...(isFillHeight
          ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }
          : null)
      }}
    >
      {message && (
        <div
          className="k-text-secondary k-mb-1"
          style={{ fontSize: 11, lineHeight: '14px' }}
        >
          {message}
        </div>
      )}
      {/* Toolbar: search (icon-based), export (right-aligned) */}
      <div className="k-d-flex k-align-items-center k-mb-2" style={{ gap: 6, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={toggleSearch} title="Search" aria-label="Search">
          <span className="k-icon k-font-icon k-i-search" />
        </Button>
        {showSearch && (
          <Input
            size="small"
            value={query}
            onChange={e => setQuery(e?.value ?? '')}
            placeholder="Search..."
            style={{ maxWidth: 220 }}
          />
        )}
        <Button size="small" onClick={downloadCsv} title="Export to CSV" aria-label="Export to CSV">
          <i className="fas fa-download" />
        </Button>
      </div>

      {/* Grid in tile */}
      <div style={isFillHeight ? { flex: '1 1 auto', minHeight: 0 } : undefined}>
        {renderGrid(paged, isFillHeight ? { height: '100%' } : {})}
      </div>

      {/* Fullscreen overlay handled by SmartChatDashboard */}
    </div>
  );
};

export default SimpleGrid;
